import { FC, useEffect, useState } from 'react';
import { TemplateEditViews } from 'modules/templateEditor/common';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { MessageMedium } from 'modules/graphql/types';
import { useCombinedState } from '../hooks';
import { InStudioTemplateProps } from '../InStudioTemplateEditing.types';
import { InStudioTakeoverContext } from './InStudioTakeoverContext';
import { isMediumDesignEnabled } from './InStudioTakeoverContextProvider.utils';

export const InStudioTakeoverContextProvider: FC<React.PropsWithChildren<any>> = ({
  children,
  ...props
}: React.PropsWithChildren<InStudioTemplateProps>) => {
  const { messageMedium } = props;
  const [isCheckingForUnsavedChanges, setCheckForUnsavedChanges] = useState(false);
  const [isSavingDesignChanges, setIsSavingDesignChanges] = useState(false);
  const [shouldCloseTakeover, setShouldCloseTakeover] = useState(false);
  const { isDirty } = useEditableFieldsContext();
  const queryParamContext = useQueryParamContext();
  const [view, setView] = useCombinedState<TemplateEditViews | string>({
    externalValue: isMediumDesignEnabled(messageMedium as MessageMedium, props.view),
    internalValue: queryParamContext.view,
    setExternalValue: props.onViewChange,
    // The type of `setInternalValue` needs to be ignored as the external value
    // is optional but the internal is not.  This should be changed to both
    // being optional in the future.
    // @ts-ignore
    setInternalValue: queryParamContext.setView,
  });
  const [templateId, setTemplateId] = useCombinedState<number | null>({
    externalValue: props.templateId,
    internalValue: queryParamContext.templateId,
    setExternalValue: props.onTemplateIdChange,
    // @ts-ignore
    setInternalValue: queryParamContext.setTemplateId,
  });
  const [campaignId, setCampaignId] = useCombinedState<number | null>({
    externalValue: props.campaignId,
    internalValue: queryParamContext.campaignId,
    setExternalValue: props.onCampaignIdChange,
    // @ts-ignore
    setInternalValue: queryParamContext.setCampaignId,
  });

  useEffect(() => {
    if (shouldCloseTakeover) {
      setShouldCloseTakeover(false);
      setCheckForUnsavedChanges(false);
    }
  }, [shouldCloseTakeover]);

  useEffect(() => {
    if ((isCheckingForUnsavedChanges || isSavingDesignChanges) && !isDirty) {
      setShouldCloseTakeover(true);
    }
  }, [isCheckingForUnsavedChanges, isSavingDesignChanges, isDirty]);

  return (
    <InStudioTakeoverContext.Provider
      value={{
        ...queryParamContext,
        isCheckingForUnsavedChanges,
        setCheckForUnsavedChanges,
        isSavingDesignChanges,
        setIsSavingDesignChanges,
        shouldCloseTakeover,
        setShouldCloseTakeover,
        view: view as TemplateEditViews,
        setView,
        templateId,
        setTemplateId,
        campaignId,
        setCampaignId,
        consumingApp: props.consumingApp,
      }}
    >
      {children}
    </InStudioTakeoverContext.Provider>
  );
};
